// configs/app_configs.yaml 是桌面 + 服务守护的唯一声明源——后端
// /api/v1/apps/registry 下发已经是"最终形态"的 AppDTO 数组(只取 yaml 里
// 声明了 app 面的 entry)。前端只需要:
//   (1) 组件 APP 按 id 在 frontend/builtin-apps/<id>/ 下懒加载入口组件;
//   (2) 组装成壳层能直接用的 ResolvedApp[]。
//
// 旧版的 manifest 校验、apps.config.json 覆盖合并、id 冲突处理都搬回了后端
// (yaml schema + 解析器),前端不再做重复校验——单一事实源。

import type {
  AppsRegistryResponse,
  ComponentAppDTO,
  ExternalAppDTO,
  Permission,
} from '@blade/os-sdk';
import type { ComponentType } from 'react';
import type { ResolvedApp, ResolvedRegistry } from '@blade/os-shell';
import { resolveServiceUrl } from '@blade/os-sdk';
import { http } from '../runtime/http';

// 组件 APP 入口索引:{ '../../builtin-apps/<id>/index.ts': () => import(...) }
// index.ts 里只 `export { default } from './App'`,所以 module.default 就是
// 要挂载到窗口里的 React 组件。
const COMPONENT_ENTRIES = import.meta.glob<{ default: ComponentType<unknown> }>(
  '../../builtin-apps/*/index.ts',
);

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  const msg = (err as Error).message ?? '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

function reloadOnce(): void {
  const key = 'blade-os:chunk-reload';
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  window.location.reload();
}

function resolveComponentLoader(
  appId: string,
): (() => Promise<ComponentType<unknown>>) | null {
  // Vite glob 的 key 是从当前文件出发的完整相对路径。
  const key = `../../builtin-apps/${appId}/index.ts`;
  const loader = COMPONENT_ENTRIES[key];
  if (!loader) return null;
  return async () => {
    try {
      const mod = await loader();
      return mod.default;
    } catch (err) {
      if (isChunkLoadError(err)) reloadOnce();
      throw err;
    }
  };
}

function toResolvedComponent(dto: ComponentAppDTO): ResolvedApp | null {
  const load = resolveComponentLoader(dto.id);
  if (!load) {
    console.warn(
      `[loadRegistry] app_configs.yaml 里声明了组件 APP id=${dto.id},但 frontend/builtin-apps/${dto.id}/ 不存在,已跳过`,
    );
    return null;
  }
  return {
    id: dto.id,
    title: dto.title,
    icon: dto.icon,
    installed: dto.installed,
    publisher: dto.publisher,
    disabled: dto.disabled,
    marketInstalled: dto.marketInstalled,
    singleton: dto.singleton,
    defaultSize: dto.defaultSize,
    load,
    permissions: (dto.permissions ?? []) as readonly Permission[],
  };
}

function toResolvedExternal(dto: ExternalAppDTO): ResolvedApp {
  return {
    id: dto.id,
    title: dto.title,
    icon: dto.icon,
    installed: dto.installed,
    publisher: dto.publisher,
    disabled: dto.disabled,
    marketInstalled: dto.marketInstalled,
    defaultSize: dto.defaultSize,
    // localhost 动态改写:127.0.0.1 换成浏览器当前访问 OS 的主机名,
    // 局域网其他用户点图标才打得开(spec 260723 v4.2 ③)。
    externalUrl: resolveServiceUrl(dto.externalUrl) ?? dto.externalUrl,
    openIn: dto.openIn,
    permissions: [],
  };
}

export interface LoadRegistryOptions {
  /** 测试注入;生产走 http().get('/apps/registry')。 */
  fetchRegistry?: () => Promise<AppsRegistryResponse>;
}

async function defaultFetchRegistry(): Promise<AppsRegistryResponse> {
  const resp = await http().get<AppsRegistryResponse>('/apps/registry');
  return resp.data;
}

export async function loadRegistry(
  options?: LoadRegistryOptions,
): Promise<ResolvedRegistry> {
  sessionStorage.removeItem('blade-os:chunk-reload');

  const data = await (options?.fetchRegistry ?? defaultFetchRegistry)();
  const entries: ResolvedApp[] = [];
  for (const app of data.apps) {
    if (app.type === 'component') {
      const resolved = toResolvedComponent(app);
      if (resolved) entries.push(resolved);
    } else {
      entries.push(toResolvedExternal(app));
    }
  }
  return { entries };
}
